import React, { useState, useEffect } from 'react';
import { Modal, Table, Pagination, Space, Tag, Spin, message, Form, Input, DatePicker, Button, Row, Col } from 'antd';
import { PhoneOutlined, UserOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { adminService } from '../services/admin';
import { formatMexicoTime } from '../utils/timeUtils';
import type { PhoneRecord, PaginationInfo } from '../types/api';

const { RangePicker } = DatePicker;

interface DatasetRecordsModalProps {
  visible: boolean;
  datasetId: number;
  datasetName: string;
  onClose: () => void;
}

const DatasetRecordsModal: React.FC<DatasetRecordsModalProps> = ({
  visible,
  datasetId,
  datasetName,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [records, setRecords] = useState<PhoneRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [duplicateStats, setDuplicateStats] = useState({
    duplicate_groups: 0,
    total_duplicates: 0,
  });
  const [filters, setFilters] = useState<{
    uploader?: string;
    phone_number?: string;
    start_date?: string;
    end_date?: string;
  }>({});

  const fetchRecords = async (page: number = 1, perPage: number = 20, currentFilters = filters) => {
    try {
      setLoading(true);
      const response = await adminService.getDatasetRecords(datasetId, {
        page,
        per_page: perPage,
        ...currentFilters,
      });
      
      setRecords(response.records);
      setDuplicateStats({
        duplicate_groups: response.duplicate_groups || 0,
        total_duplicates: response.total_duplicates || 0,
      });
      
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('获取记录失败:', error);
      message.error('获取记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && datasetId) {
      // 重置筛选条件
      setFilters({});
      form.resetFields();
      fetchRecords();
    }
  }, [visible, datasetId]);

  const handlePageChange = (page: number, pageSize?: number) => {
    fetchRecords(page, pageSize || pagination.per_page, filters);
  };

  const handlePageSizeChange = (current: number, size: number) => {
    fetchRecords(1, size, filters);
  };

  const handleSearch = (values: any) => {
    const newFilters: any = {};
    
    if (values.uploader) {
      newFilters.uploader = values.uploader.trim();
    }
    
    if (values.phone_number) {
      newFilters.phone_number = values.phone_number.trim();
    }
    
    if (values.date_range && values.date_range.length === 2) {
      // date_range是Dayjs对象数组
      newFilters.start_date = values.date_range[0].startOf('day').toISOString();
      newFilters.end_date = values.date_range[1].endOf('day').toISOString();
    }
    
    setFilters(newFilters);
    fetchRecords(1, pagination.per_page, newFilters);
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({});
    fetchRecords(1, pagination.per_page, {});
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '电话号码',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone: string) => (
        <Space>
          <PhoneOutlined />
          {phone}
        </Space>
      ),
    },
    {
      title: '上传者',
      dataIndex: 'uploader',
      key: 'uploader',
      render: (uploader: string) => (
        <Space>
          <UserOutlined />
          {uploader}
        </Space>
      ),
    },
    {
      title: '上传时间',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      render: (date: string) => formatMexicoTime(date),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <span>数据集记录查看</span>
          <Tag color="blue">{datasetName}</Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1100}
      footer={null}
      destroyOnClose
    >
      {/* 筛选表单 */}
      <Form
        form={form}
        onFinish={handleSearch}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="uploader" style={{ marginBottom: 0 }}>
              <Input placeholder="上传者名字" prefix={<UserOutlined />} allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="phone_number" style={{ marginBottom: 0 }}>
              <Input placeholder="电话号码" prefix={<PhoneOutlined />} allowClear />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="date_range" style={{ marginBottom: 0 }}>
              <RangePicker 
                style={{ width: '100%' }}
                placeholder={['开始时间', '结束时间']}
                showTime={false}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <span>总记录数: <strong>{pagination.total}</strong></span>
          <span>重复号码组数: <strong>{duplicateStats.duplicate_groups}</strong></span>
          <span>重复记录总数: <strong>{duplicateStats.total_duplicates}</strong></span>
        </Space>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
        />
      </Spin>

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Pagination
          current={pagination.page}
          total={pagination.total}
          pageSize={pagination.per_page}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) =>
            `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
          }
          pageSizeOptions={['10', '20', '50', '100']}
          onChange={handlePageChange}
          onShowSizeChange={handlePageSizeChange}
        />
      </div>
    </Modal>
  );
};

export default DatasetRecordsModal;
